import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';
const defaultForm = {
    providerId: '',
    name: '',
    content: '',
    model: '',
    isActive: true,
};
function truncate(str, max) {
    if (str.length <= max)
        return str;
    return `${str.slice(0, max)}…`;
}
export function AIPrompts() {
    const { accessToken } = useAuthStore();
    const [prompts, setPrompts] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(defaultForm);
    const [modelOptions, setModelOptions] = useState([]);
    const [modelsLoading, setModelsLoading] = useState(false);
    const [modelsError, setModelsError] = useState('');
    const fetchProviders = useCallback(async () => {
        const response = await fetch('/api/admin/ai-providers', {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok)
            throw new Error('AI provider listesi alınamadı');
        const data = (await response.json());
        setProviders(data.map((p) => ({ id: p.id, name: p.name })));
    }, [accessToken]);
    const fetchPrompts = useCallback(async () => {
        const response = await fetch('/api/admin/ai-prompts', {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok)
            throw new Error('Prompt listesi alınamadı');
        const data = (await response.json());
        setPrompts(data);
    }, [accessToken]);
    const loadAll = useCallback(async () => {
        try {
            await Promise.all([fetchProviders(), fetchPrompts()]);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Yükleme hatası');
        }
        finally {
            setLoading(false);
        }
    }, [fetchProviders, fetchPrompts]);
    useEffect(() => {
        loadAll();
    }, [loadAll]);
    const fetchProviderModels = useCallback(async (providerId, signal) => {
        const pid = parseInt(providerId, 10);
        if (!Number.isFinite(pid) || pid <= 0) {
            setModelOptions([]);
            setModelsError('');
            setModelsLoading(false);
            return;
        }
        setModelsLoading(true);
        setModelsError('');
        try {
            const r = await fetch(`/api/admin/ai-providers/${pid}/models`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                signal,
            });
            const data = (await r.json());
            if (signal?.aborted)
                return;
            setModelOptions(Array.isArray(data.models) ? data.models : []);
            setModelsError(typeof data.error === 'string' ? data.error : '');
        }
        catch (err) {
            if (signal?.aborted || (err instanceof DOMException && err.name === 'AbortError'))
                return;
            setModelOptions([]);
            setModelsError('Modeller alınamadı');
        }
        finally {
            if (!signal?.aborted)
                setModelsLoading(false);
        }
    }, [accessToken]);
    useEffect(() => {
        if (!showModal)
            return;
        const ac = new AbortController();
        void fetchProviderModels(formData.providerId, ac.signal);
        return () => ac.abort();
    }, [showModal, formData.providerId, fetchProviderModels]);
    const handleOpenModal = (row) => {
        setError('');
        setModelsError('');
        setModelOptions([]);
        if (row) {
            setEditing(row);
            setFormData({
                providerId: String(row.providerId),
                name: row.name,
                content: row.content,
                model: row.model ?? '',
                isActive: row.isActive,
            });
        }
        else {
            setEditing(null);
            const firstId = providers[0]?.id;
            setFormData({
                ...defaultForm,
                providerId: firstId !== undefined ? String(firstId) : '',
            });
        }
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setEditing(null);
        setFormData(defaultForm);
        setModelOptions([]);
        setModelsError('');
        setModelsLoading(false);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const providerId = parseInt(formData.providerId, 10);
        if (!Number.isFinite(providerId) || providerId <= 0) {
            setError('Geçerli bir provider seçin');
            return;
        }
        const modelTrim = formData.model.trim();
        const body = {
            providerId,
            name: formData.name.trim(),
            content: formData.content,
            isActive: formData.isActive,
            model: modelTrim.length > 0 ? modelTrim : null,
        };
        setActionLoading(editing?.id ?? -1);
        try {
            const url = editing ? `/api/admin/ai-prompts/${editing.id}` : '/api/admin/ai-prompts';
            const method = editing ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => ({})));
                throw new Error(data.message || 'Kayıt başarısız');
            }
            handleCloseModal();
            await loadAll();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Kayıt başarısız');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDelete = async (row) => {
        if (!confirm(`"${row.name}" promptunu silmek istediğinize emin misiniz?`))
            return;
        setActionLoading(row.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/ai-prompts/${row.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => ({})));
                throw new Error(data.message || 'Silinemedi');
            }
            await loadAll();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Silinemedi');
        }
        finally {
            setActionLoading(null);
        }
    };
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "AI Prompts" }), _jsx("p", { children: "\u015Eablon prompt (provider, model, isim, i\u00E7erik). Film / oturum verisi \u00E7al\u0131\u015Fma an\u0131nda kod taraf\u0131nda eklenir; modeller sa\u011Flay\u0131c\u0131 API\u2019sinden \u00E7ekilir." })] }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), disabled: providers.length === 0, title: providers.length === 0 ? 'Önce en az bir AI provider ekleyin' : undefined, children: "+ Yeni Prompt" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["Promptlar (", prompts.length, ")"] }) }), providers.length === 0 ? (_jsx("div", { className: "admin-empty-state", children: _jsx("p", { children: "Prompt eklemek i\u00E7in \u00F6nce AI Providers sayfas\u0131ndan en az bir sa\u011Flay\u0131c\u0131 tan\u0131mlay\u0131n." }) })) : prompts.length === 0 ? (_jsxs("div", { className: "admin-empty-state", children: [_jsx("p", { children: "Hen\u00FCz prompt yok." }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "\u0130lk promptu ekle" })] })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Provider" }), _jsx("th", { children: "\u0130sim" }), _jsx("th", { children: "Model" }), _jsx("th", { children: "\u0130\u00E7erik" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "\u0130\u015Flemler" })] }) }), _jsx("tbody", { children: prompts.map((row) => (_jsxs("tr", { children: [_jsx("td", { children: row.providerName }), _jsx("td", { children: _jsx("span", { className: "provider-name", children: row.name }) }), _jsx("td", { children: _jsx("code", { className: "provider-api-key", children: row.model?.trim() ? row.model : '—' }) }), _jsx("td", { children: _jsx("span", { className: "provider-base-url", title: row.content, children: truncate(row.content, 64) }) }), _jsx("td", { children: _jsx("span", { className: "provider-type-badge", children: row.isActive ? 'Aktif' : 'Pasif' }) }), _jsx("td", { children: _jsxs("div", { className: "admin-action-buttons", children: [_jsx("button", { type: "button", className: "admin-edit-btn", onClick: () => handleOpenModal(row), disabled: actionLoading === row.id, title: "D\u00FCzenle", children: "\u270F\uFE0F" }), _jsx("button", { type: "button", className: "admin-delete-btn", onClick: () => handleDelete(row), disabled: actionLoading === row.id, title: "Sil", children: actionLoading === row.id ? '...' : '🗑' })] }) })] }, row.id))) })] }) }))] }), showModal && (_jsx("div", { className: "admin-modal-overlay", onClick: handleCloseModal, children: _jsxs("div", { className: "admin-modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "admin-modal-header", children: [_jsx("h2", { children: editing ? 'Prompt düzenle' : 'Yeni prompt' }), _jsx("button", { type: "button", className: "admin-modal-close", onClick: handleCloseModal, children: "\u2715" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "admin-form", children: [_jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["AI Provider *", _jsxs("select", { className: "admin-form-select", value: formData.providerId, onChange: (e) => setFormData({ ...formData, providerId: e.target.value, model: '' }), required: true, children: [_jsx("option", { value: "", disabled: true, children: "Se\u00E7in\u2026" }), providers.map((p) => (_jsx("option", { value: p.id, children: p.name }, p.id)))] })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Model", _jsxs("div", { className: "admin-form-row", style: { display: 'flex', gap: 8, alignItems: 'center' }, children: [modelOptions.length > 0 ? (_jsxs("select", { className: "admin-form-select", value: modelOptions.some((m) => m.id === formData.model)
                                                            ? formData.model
                                                            : formData.model
                                                                ? '__custom__'
                                                                : '', onChange: (e) => {
                                                            const v = e.target.value;
                                                            if (v === '__custom__') {
                                                                setFormData({ ...formData, model: formData.model && !modelOptions.some((m) => m.id === formData.model) ? formData.model : '' });
                                                                return;
                                                            }
                                                            setFormData({ ...formData, model: v });
                                                        }, disabled: modelsLoading || !formData.providerId, children: [_jsx("option", { value: "", children: "(provider varsay\u0131lan\u0131 / bo\u015F)" }), modelOptions.map((m) => (_jsx("option", { value: m.id, children: m.label && m.label !== m.id ? `${m.label} (${m.id})` : m.id }, m.id))), _jsx("option", { value: "__custom__", children: "\u00D6zel model ID\u2026" })] })) : (_jsx("input", { type: "text", className: "admin-form-input", value: formData.model, onChange: (e) => setFormData({ ...formData, model: e.target.value }), placeholder: modelsLoading ? 'Modeller yükleniyor…' : 'Model ID (opsiyonel)', autoComplete: "off", disabled: modelsLoading })), _jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", disabled: !formData.providerId || modelsLoading, title: "Provider\u2019dan model listesini yenile", onClick: () => void fetchProviderModels(formData.providerId), children: modelsLoading ? '…' : 'Yenile' })] }), modelOptions.length > 0 &&
                                                formData.model !== '' &&
                                                !modelOptions.some((m) => m.id === formData.model) && (_jsx("input", { type: "text", className: "admin-form-input", style: { marginTop: 8 }, value: formData.model, onChange: (e) => setFormData({ ...formData, model: e.target.value }), placeholder: "\u00D6zel model ID", autoComplete: "off" })), modelsLoading && (_jsx("small", { className: "admin-form-hint", children: "Sa\u011Flay\u0131c\u0131 API\u2019sinden modeller al\u0131n\u0131yor\u2026" })), !modelsLoading && modelsError && (_jsxs("small", { className: "admin-form-hint", children: [modelsError, " \u2014 model ID\u2019yi elle girebilirsiniz."] })), !modelsLoading && !modelsError && modelOptions.length === 0 && formData.providerId && (_jsx("small", { className: "admin-form-hint", children: "Bu provider i\u00E7in liste bo\u015F; model ID\u2019yi elle yaz\u0131n veya bo\u015F b\u0131rak\u0131n." })), !modelsLoading && !modelsError && modelOptions.length > 0 && (_jsxs("small", { className: "admin-form-hint", children: [modelOptions.length, " model listelendi."] }))] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["\u0130sim *", _jsx("input", { type: "text", className: "admin-form-input", value: formData.name, onChange: (e) => setFormData({ ...formData, name: e.target.value }), required: true, placeholder: "\u00D6rn: intent_first_message", maxLength: 128 })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["\u0130\u00E7erik *", _jsx("textarea", { className: "admin-form-textarea", value: formData.content, onChange: (e) => setFormData({ ...formData, content: e.target.value }), required: true, rows: 12, placeholder: "Prompt metni\u2026" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-setting-checkbox", children: [_jsx("input", { type: "checkbox", checked: formData.isActive, onChange: (e) => setFormData({ ...formData, isActive: e.target.checked }) }), _jsx("span", { children: "Aktif" })] }) }), _jsxs("div", { className: "admin-form-actions", children: [_jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", onClick: handleCloseModal, disabled: actionLoading !== null, children: "\u0130ptal" }), _jsx("button", { type: "submit", className: "admin-btn admin-btn-primary", disabled: actionLoading !== null, children: actionLoading !== null ? 'Kaydediliyor…' : editing ? 'Güncelle' : 'Kaydet' })] })] })] }) }))] }));
}
